import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AuthenticatedUser, LoginRequest, PermissionCode } from '@oxxean/types';
import * as authApi from '../lib/api/auth';
import { isUnauthorized } from '../lib/errors';
import { onSessionExpired } from '../lib/http';
import { queryKeys } from '../lib/query-client';

interface AuthContextValue {
  user: AuthenticatedUser | null;
  isLoading: boolean;
  /** true cuando ya se resolvió el primer `GET /auth/me`. */
  isResolved: boolean;
  login: (payload: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  can: (permission: PermissionCode) => boolean;
  canAny: (permissions: PermissionCode[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const queryClient = useQueryClient();

  /**
   * La sesión se conoce por `GET /api/auth/me`, no por inspeccionar un token: el
   * navegador no puede leer la cookie de acceso (DEC-A05).
   *
   * Un 401 se traduce a `null` —«no hay sesión»— en lugar de propagarse como
   * error. Si se dejara fallar, la consulta quedaría en estado de error y cada
   * limpieza de caché dispararía un intento nuevo: 401, refresco, limpieza, 401…
   * Devolver `null` corta ese ciclo y deja el caso «visitante anónimo» como un
   * resultado válido, que es lo que realmente es.
   */
  const meQuery = useQuery({
    queryKey: queryKeys.me,
    queryFn: async (): Promise<AuthenticatedUser | null> => {
      try {
        return await authApi.fetchCurrentUser();
      } catch (error) {
        if (isUnauthorized(error)) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    // El cliente HTTP avisa cuando el refresco falló. Se descartan los datos del
    // resto de la aplicación, pero NO la consulta de sesión: quitarla provocaría
    // un intento inmediato y otro 401.
    return onSessionExpired(() => {
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'auth' });
      queryClient.setQueryData(queryKeys.me, null);
    });
  }, [queryClient]);

  const loginMutation = useMutation({
    mutationFn: authApi.login,
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.me, data.user);
    },
  });

  const login = useCallback(
    async (payload: LoginRequest) => {
      await loginMutation.mutateAsync(payload);
    },
    [loginMutation],
  );

  const logout = useCallback(async () => {
    await authApi.logout();
    queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'auth' });
    queryClient.setQueryData(queryKeys.me, null);
  }, [queryClient]);

  const refreshUser = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.me });
  }, [queryClient]);

  const user = meQuery.data ?? null;

  const value = useMemo<AuthContextValue>(() => {
    const permissions = new Set<PermissionCode>(user?.permissions ?? []);
    return {
      user,
      isLoading: meQuery.isLoading,
      isResolved: meQuery.isFetched,
      login,
      logout,
      refreshUser,
      can: (permission) => permissions.has(permission),
      canAny: (list) => list.some((permission) => permissions.has(permission)),
    };
  }, [user, meQuery.isLoading, meQuery.isFetched, login, logout, refreshUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth debe usarse dentro de <AuthProvider>.');
  return context;
}

/**
 * Comprobación de permiso para ocultar controles.
 *
 * Es sólo comodidad: la autorización real se aplica en el endpoint (principio 6).
 */
export function usePermission(permission: PermissionCode | undefined): boolean {
  const { can } = useAuth();
  return permission === undefined ? true : can(permission);
}
